import { Stack, Box, Group, Button, Textarea } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { TimeInput } from '@mantine/dates';
import { Coding, Location } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { createReference, getReferenceString } from '@medplum/core';
import type { Reference, Slot } from '@medplum/fhirtypes';
import { useCallback, useState, useEffect } from 'react';
import { MedplumClient } from '@medplum/core';
import { VIRTUAL_ENCOUNTER_CLASS_CODING } from '@bariendo/lib/codings';
import { fetchBot } from '@/services/services';
import { alert } from '@/services/alert';
import { OneTimeAvailabilityFormData } from './types';
import { AppointmentTypesInput } from '@/pages/SchedulingPage/components/AppointmentTypesInput';
import { convertTimeToHHMMSS } from '../utils/availability';
import { getBrowserTimezone } from '../utils/common';
import { AvailabilityDrawerSkeleton } from './AvailabilityDrawerSkeleton';
import { SharedAvailabilityFields } from './SharedAvailabilityFields';
import { validateSharedAvailabilityFields, ValidationResult } from './validateSharedAvailabilityFields';
import { AvailabilityTypeEnum, SetOneTimeAvailabilityRequest } from '@bariendo/lib/scheduling';
import dayjs from 'dayjs';
import useSlotData from '../hooks/useSlotData';
import { AvailabilityType } from '@bariendo/lib/scheduling';
import { PractitionerWithRole } from '../types';

function validateOneTimeAvailability(formData: OneTimeAvailabilityFormData): ValidationResult {
  // Validate shared fields first
  const sharedValidation = validateSharedAvailabilityFields({
    practitioner: formData.practitioner,
    timezone: formData.timezone,
    encounterClass: formData.encounterClass,
    location: formData.location,
  });

  if (!sharedValidation.valid) {
    return sharedValidation;
  }

  if (!formData.date) {
    return { valid: false, error: 'Date is required' };
  }

  const selectedDate = dayjs(formData.date);
  const today = dayjs().startOf('day');
  if (selectedDate.isBefore(today)) {
    return { valid: false, error: 'Date cannot be in the past' };
  }

  if (!formData.startTime) {
    return { valid: false, error: 'Start time is required' };
  }

  if (!formData.endTime) {
    return { valid: false, error: 'End time is required' };
  }

  const start = convertTimeToHHMMSS(formData.startTime);
  const end = convertTimeToHHMMSS(formData.endTime);

  if (start >= end) {
    return { valid: false, error: 'End time must be after start time' };
  }

  if (!formData.appointmentTypes || formData.appointmentTypes.length === 0) {
    return { valid: false, error: 'At least one appointment type must be selected' };
  }

  return { valid: true };
}

/**
 * Saves one-time availability by calling the set-one-time-availability bot.
 * When slotReference is provided, the bot updates that Slot instead of creating a new one.
 * @param medplum - Medplum client instance
 * @param formData - The one-time availability form data
 * @param slotReference - Optional Slot reference for editing; when set, the bot updates this Slot
 */
async function saveOneTimeAvailability(
  medplum: MedplumClient,
  formData: OneTimeAvailabilityFormData,
  slotReference?: Reference<Slot>
): Promise<void> {
  const validation = validateOneTimeAvailability(formData);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  const trimmedGuideline = formData.schedulingGuideline?.trim() ?? '';
  const botInput: SetOneTimeAvailabilityRequest = {
    practitioner: formData.practitioner!,
    encounterClass: formData.encounterClass,
    timezone: formData.timezone,
    ...(formData.location ? { location: formData.location } : {}),
    date: formData.date,
    startTime: convertTimeToHHMMSS(formData.startTime),
    endTime: convertTimeToHHMMSS(formData.endTime),
    appointmentTypes: formData.appointmentTypes,
    // On edit, always send the guideline (incl. empty) so clearing the textarea actually clears the note —
    // the bot preserves an *omitted* guideline. On create there's nothing to clear, so omit when empty.
    ...(slotReference
      ? { schedulingGuideline: trimmedGuideline }
      : trimmedGuideline
        ? { schedulingGuideline: trimmedGuideline }
        : {}),
    ...(slotReference ? { slot: slotReference } : {}),
  };

  await fetchBot(medplum, 'set-one-time-availability', botInput);
}

interface OneTimeAvailabilityFormProps {
  initialPractitioner: PractitionerWithRole | null;
  onClose: () => void;
  slotIdToEdit?: string | null;
  onSaveSuccess: (providerId: string | undefined, type: AvailabilityType) => void;
  practitioners?: PractitionerWithRole[];
  /** When true, restrict the appointment-type selector to bariatric procedure types only. */
  proceduresOnly?: boolean;
}

export function OneTimeAvailabilityForm({
  initialPractitioner,
  onClose,
  slotIdToEdit = null,
  onSaveSuccess,
  practitioners,
  proceduresOnly = false,
}: OneTimeAvailabilityFormProps) {
  const medplum = useMedplum();
  const browserTimezone = getBrowserTimezone();
  // Shared fields state
  const [practitioner, setPractitioner] = useState<PractitionerWithRole | null>(initialPractitioner);
  const [encounterClass, setEncounterClass] = useState<Coding>(VIRTUAL_ENCOUNTER_CLASS_CODING);
  const [location, setLocation] = useState<Location | null>(null);
  const [timezone, setTimezone] = useState<string>(browserTimezone);

  // Form-specific state
  const getDefaultTimes = () => {
    const now = dayjs();
    const start = now.startOf('hour').format('HH:mm');
    const end = now.startOf('hour').add(1, 'hour').format('HH:mm');
    return { start, end };
  };

  const [date, setDate] = useState<Date | null>(() => new Date());
  const [startTime, setStartTime] = useState<string>(() => getDefaultTimes().start);
  const [endTime, setEndTime] = useState<string>(() => getDefaultTimes().end);
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<Coding[]>([]);
  const [schedulingGuideline, setSchedulingGuideline] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const { slotData, isLoadingSlot } = useSlotData(slotIdToEdit, initialPractitioner);

  useEffect(() => {
    if (slotData && !isLoadingSlot) {
      setPractitioner(slotData.practitioner);
      setEncounterClass(slotData.encounterClass);
      setLocation(slotData.location);
      setTimezone(slotData.timezone);
      setDate(dayjs(slotData.date).toDate());
      setStartTime(slotData.startTime);
      setSelectedAppointmentTypes(slotData.appointmentTypes);
      setEndTime(slotData.endTime);
      setSchedulingGuideline(slotData.schedulingGuideline ?? '');
    }
  }, [slotData, isLoadingSlot]);

  // Update initial practitioner when prop changes
  useEffect(() => {
    if (initialPractitioner) {
      setPractitioner(initialPractitioner);
    }
  }, [initialPractitioner]);

  // Create a TimeRange-like object for AppointmentTypesInput
  const timeRangeForAppointmentTypes = {
    startTime,
    endTime,
    appointmentTypes: selectedAppointmentTypes,
  };

  const handleSave = useCallback(async () => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      const completeData: OneTimeAvailabilityFormData = {
        practitioner: practitioner ? createReference(practitioner) : null,
        encounterClass,
        timezone,
        location: location ? createReference(location) : null,
        date: dayjs(date).format('YYYY-MM-DD'),
        startTime,
        endTime,
        appointmentTypes: selectedAppointmentTypes,
        schedulingGuideline,
      };

      const slotReference = slotIdToEdit
        ? createReference({ resourceType: 'Slot', id: slotIdToEdit } as Slot)
        : undefined;
      await saveOneTimeAvailability(medplum, completeData, slotReference);

      if (practitioner) {
        onSaveSuccess(getReferenceString(practitioner), AvailabilityTypeEnum.ONETIME);
      }
      onClose();
    } catch (error) {
      alert.error(`Couldn't save availability: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  }, [
    practitioner,
    encounterClass,
    timezone,
    location,
    date,
    startTime,
    endTime,
    selectedAppointmentTypes,
    schedulingGuideline,
    medplum,
    isSaving,
    onClose,
    onSaveSuccess,
    slotIdToEdit,
  ]);

  if (slotIdToEdit && isLoadingSlot) {
    return <AvailabilityDrawerSkeleton availabilityType={AvailabilityTypeEnum.ONETIME} />;
  }

  return (
    <Stack gap="sm">
      <SharedAvailabilityFields
        practitioner={practitioner}
        encounterClass={encounterClass}
        location={location}
        timezone={timezone}
        onPractitionerChange={setPractitioner}
        onVisitTypeChange={setEncounterClass}
        onLocationChange={setLocation}
        onTimezoneChange={setTimezone}
        practitioners={practitioners}
      />

      <Box>
        <Stack gap="md">
          {/* Date */}
          <DatePickerInput
            label="Date"
            placeholder="Select date"
            value={date}
            onChange={(value) => setDate(value as Date | null)}
            minDate={new Date()}
            clearable={false}
            required
          />

          {/* Start Time and End Time */}
          <Group grow>
            <Box>
              <TimeInput
                label="Start time"
                value={startTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartTime(e.currentTarget.value)}
                required
              />
            </Box>
            <Box>
              <TimeInput
                label="End time"
                value={endTime}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEndTime(e.currentTarget.value)}
                required
              />
            </Box>
          </Group>

          {/* Appointment Types */}
          <AppointmentTypesInput
            timeRange={timeRangeForAppointmentTypes}
            onChange={setSelectedAppointmentTypes}
            collapsible={false}
            selectAllByDefault
            providerRoleCode={practitioner?.roleCode}
            proceduresOnly={proceduresOnly}
          />

          {/* Scheduler-facing note shown when booking into this window. */}
          <Textarea
            label="Scheduling guideline"
            placeholder="e.g. Only book IGB removals before noon"
            value={schedulingGuideline}
            onChange={(e) => setSchedulingGuideline(e.currentTarget.value)}
            autosize
            minRows={2}
          />
        </Stack>
      </Box>

      <Group justify="flex-end" mt="lg">
        <Button variant="filled" size="sm" radius="xl" color="#228be6" onClick={handleSave} loading={isSaving}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
